import type { GraphIndex, GraphPath } from '../lib/graph.js';
import type { AlternativeDocumentedPath } from '../lib/story-graph-models.js';
import { storyPathKey } from '../lib/story-playback.js';

type RouteChoicesProps = {
  primaryPath: GraphPath;
  alternatives: readonly AlternativeDocumentedPath[];
  selectedPath: GraphPath;
  index: GraphIndex;
  onChoose: (path: GraphPath) => void;
};

function routeChain(path: GraphPath, index: GraphIndex): string {
  const names = path.people.map((id) => index.peopleById.get(id)?.name ?? id);
  if (names.length <= 5) {
    return names.join(' → ');
  }
  return [...names.slice(0, 2), '…', ...names.slice(-2)].join(' → ');
}

export function RouteChoices({
  primaryPath,
  alternatives,
  selectedPath,
  index,
  onChoose,
}: RouteChoicesProps) {
  const routes = [
    { path: primaryPath, label: 'Preferred source-linked route' },
    ...alternatives.map((alternative) => ({
      path: alternative.path,
      label: alternative.labelText,
    })),
  ];
  const selectedKey = storyPathKey(selectedPath);

  return (
    <section className="route-choices" aria-label="Source-linked route choices">
      {routes.length > 1 ? (
        <details className="route-choices__routes">
          <summary>Routes ({routes.length.toLocaleString('en-US')})</summary>
          <div className="route-choices__options">
            {routes.map((route, routeIndex) => {
              const key = storyPathKey(route.path) ?? `route-${routeIndex}`;
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={key === selectedKey}
                  onClick={() => onChoose(route.path)}
                >
                  <strong>{route.label}</strong>
                  <span>
                    {route.path.relationships.length}{' '}
                    {route.path.relationships.length === 1 ? 'step' : 'steps'}
                  </span>
                  <small>{routeChain(route.path, index)}</small>
                </button>
              );
            })}
          </div>
        </details>
      ) : null}
      <details className="route-choices__why">
        <summary>Why this route?</summary>
        <p>
          Entangled Vega chooses routes deterministically from the enabled approved relationship
          lenses. It favors primary-provenance relationship quality and recognizable intermediaries
          rather than minimizing steps alone. When alternatives appear, they are other valid
          source-linked routes in this data snapshot—not competing versions of the truth.
        </p>
      </details>
    </section>
  );
}
